import React, { useEffect, useState } from 'react';
import { Briefcase } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { BrandDashboard } from '../components/BrandDashboard';
import { useStore } from '../lib/store';
import { onDataChanged } from '../lib/liveSync';
import { fetchAdminAuth, fetchAdminSnapshot, DEFAULT_ADMIN_AUTH } from '../lib/supabase';
import type { AdminUser, AdminCatch, AdminReview } from '../lib/supabase';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/bcWTzmedNUNKBwzbD46o8N/2.png';
/**
 * STANDALONE BRAND PORTAL (dark) — lives at /brand, fully separate from the
 * admin platform. A brand signs in with their brand name + the brand password
 * (stored in Supabase) and sees ONLY their own slice via <BrandDashboard/>.
 *
 * Supabase is the single source of truth: the joined snapshot (users/catches/
 * reviews) is fetched here and polled live; drops come from the shared store.
 */
export function BrandLogin() {
  const {
    events,
    recurringQuestions,
    hydrateDrops
  } = useStore();
  const [loggedInBrand, setLoggedInBrand] = useState<string | null>(null);
  const [brandName, setBrandName] = useState('');
  const [brandPassword, setBrandPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [authConfig, setAuthConfig] = useState(DEFAULT_ADMIN_AUTH);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [catches, setCatches] = useState<AdminCatch[]>([]);
  const [reviews, setReviews] = useState<AdminReview[]>([]);
  useEffect(() => {
    let active = true;
    fetchAdminAuth().then(cfg => {
      if (active) setAuthConfig(cfg);
    });
    return () => {
      active = false;
    };
  }, []);
  const loadData = async () => {
    const [snap] = await Promise.all([fetchAdminSnapshot(), hydrateDrops()]);
    setUsers(snap.users);
    setCatches(snap.catches);
    setReviews(snap.reviews);
  };
  // Poll the joined snapshot live once a brand is signed in, and refresh
  // instantly when data changes on any other surface (admin/user/partner).
  useEffect(() => {
    if (!loggedInBrand) return;
    loadData();
    const interval = setInterval(loadData, 10000);
    const unsub = onDataChanged(() => loadData());
    return () => {
      clearInterval(interval);
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInBrand]);
  // Map question ids → labels (recurring + per-drop) so the dashboard can show
  // collected answers with their question text.
  const questionLabelMap = (() => {
    const map: Record<string, string> = {};
    (recurringQuestions || []).forEach(q => {
      map[q.id] = q.label;
    });
    events.forEach(e => (e.customQuestions || []).forEach(q => {
      map[q.id] = q.label;
    }));
    return map;
  })();
  if (loggedInBrand) {
    return <BrandDashboard brand={loggedInBrand} events={events} catches={catches} reviews={reviews} users={users} questionLabelMap={questionLabelMap} onLogout={() => {
      setLoggedInBrand(null);
      setBrandName('');
      setBrandPassword('');
    }} data-id="element-1737" data-mp="1g27kgrumdzwu" />;
  }
  return <div className="min-h-screen w-full bg-gray-950 flex flex-col items-center justify-center p-4 text-white" data-id="element-1738" data-mp="32wji1snocpoq">
      <div className="w-64 sm:w-72 max-w-full mb-8" data-id="element-1739" data-mp="37wysabcnth2h">
        <img src={LOGO_URL} alt="GONE FISHING" className="w-full h-auto object-contain" data-id="element-1740" data-mp="37tjfxgadem4r" />
      </div>
      <div className="bg-gray-900 border border-gray-800 p-8 rounded-2xl w-full max-w-sm" data-id="element-1741" data-mp="3htabllao376f">
        <div className="flex items-center justify-center mb-5" data-id="element-1742" data-mp="3mkjv045mhe9v">
          <span className="inline-flex items-center gap-2 bg-brand-blue/15 text-brand-blue text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full" data-id="element-1743" data-mp="3mn65ocnt89wb">
            <Briefcase className="w-4 h-4" data-id="element-1744" data-mp="3vn1jkp8r2j01" /> Brand Portal
          </span>
        </div>
        <p className="text-gray-400 text-sm text-center mb-5" data-id="element-1745" data-mp="4g8g1eqhi7sc">
          Brands see only their own data. Enter your brand name and password.
        </p>
        <form onSubmit={e => {
        e.preventDefault();
        const name = brandName.trim();
        if (!name) {
          setLoginError('Enter your brand name.');
          return;
        }
        if (brandPassword !== authConfig.brandPassword) {
          setLoginError('Wrong brand password.');
          return;
        }
        setLoginError('');
        setLoggedInBrand(name);
      }} data-id="element-1746" data-mp="3hyrfde7gkuc5">
          <Input placeholder="Brand name (e.g. NOCCO)" value={brandName} onChange={e => setBrandName(e.target.value)} className="bg-gray-800 border-gray-700 text-white mb-3 text-center" autoFocus list="brand-options" data-id="element-1747" data-mp="2z1q7nlmisg4i" />
          <datalist id="brand-options" data-id="element-1748" data-mp="t543cw0zq7t1">
            {Array.from(new Set(events.map(e => e.brand))).filter(Boolean).map(b => <option key={b} value={b} data-id="element-1749" data-mp="vqbrpysifx3z" />)}
          </datalist>
          <Input type="password" placeholder="Brand password" value={brandPassword} onChange={e => setBrandPassword(e.target.value)} className="bg-gray-800 border-gray-700 text-white mb-4 text-center" data-id="element-1750" data-mp="10qzhyi8x09x5" />
          {loginError && <p className="text-red-400 text-xs mb-3 text-center" data-id="element-1751" data-mp="1ehcrgp1gfwol">
              {loginError}
            </p>}
          <Button type="submit" className="w-full" data-id="element-1752" data-mp="1jfwfcuzlzijl">
            View my data
          </Button>
        </form>
      </div>
    </div>;
}